// O projectService do eslint não resolve os tipos de `sweetalert2` nem de
// `vue3-toastify` quando importados de um `.ts` (tipa os dois como `error`, daí
// os no-unsafe-*). O `vue-tsc` resolve e o build passa — mesma situação já
// documentada em `utils/audio/ffmpegM4a.worker.ts`. Nos `.vue` isso não aparece
// porque o eslint ignora `.vue` neste repo.
/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
import Swal from 'sweetalert2';
import { toast } from 'vue3-toastify';

/**
 * Resultado de "buscar a foto do contato" e o que dizer ao operador.
 *
 * O servidor passou a devolver `motivo` quando a conta não tem NENHUM canal
 * capaz de consultar a foto de um contato. Antes a rota respondia
 * `{ url: null }` nos dois casos — "não achei foto" e "nem procurei" — e as três
 * telas que chamam isso exibiam "Foto atualizada!" de qualquer jeito.
 *
 * O caso que mais morde é a API Oficial: a Cloud API **não expõe** a foto de
 * perfil dos contatos (restrição de privacidade da Meta — a mensagem recebida
 * traz só o nome). Numa conta só-oficial o botão nunca funcionou e nunca vai
 * funcionar; dizer isso é melhor que fingir sucesso.
 */
export type MotivoFotoIndisponivel =
    | 'CLOUD_API_SEM_FOTO_DE_CONTATO'
    | 'CANAL_SEM_FOTO_DE_CONTATO';

export interface RespostaFotoDeContato {
    url: string | null;
    motivo?: MotivoFotoIndisponivel;
}

const EXPLICACOES: Record<MotivoFotoIndisponivel, { titulo: string; html: string }> = {
    CLOUD_API_SEM_FOTO_DE_CONTATO: {
        titulo: 'Foto indisponível neste canal',
        html:
            '<p style="font-size:14px;margin-bottom:10px;">A <strong>API Oficial do WhatsApp</strong> (Meta Cloud API) ' +
            'não disponibiliza a foto de perfil dos contatos. É uma restrição de privacidade da Meta: ' +
            'a mensagem recebida traz apenas o <em>nome</em> do contato.</p>' +
            '<p style="font-size:13px;color:#6c757d;margin:0;">Para ter foto de contato é preciso um canal ' +
            'conectado por QR Code (Evolution ou Coexistência Híbrida) na mesma conta.</p>',
    },
    CANAL_SEM_FOTO_DE_CONTATO: {
        titulo: 'Foto indisponível neste canal',
        html:
            '<p style="font-size:14px;margin-bottom:10px;">Nenhum dos canais desta conta consulta a foto de perfil ' +
            'de contatos.</p>' +
            '<p style="font-size:13px;color:#6c757d;margin:0;">A busca de foto hoje só existe em canais de WhatsApp ' +
            'conectados por QR Code (Evolution ou Coexistência Híbrida).</p>',
    },
};

/**
 * Traduz o resultado da busca em UMA reação, e só uma.
 *
 * - `motivo` → explica por que não dá, e não chama de erro: não é falha, é
 *   limite do canal;
 * - `url` → sucesso de verdade;
 * - nem um nem outro → o contato simplesmente não tem foto pública.
 */
export async function reagirAoResultadoDaFoto(resultado: RespostaFotoDeContato | undefined): Promise<void> {
    if (resultado?.motivo) {
        const { titulo, html } = EXPLICACOES[resultado.motivo];
        await Swal.fire({ title: titulo, icon: 'info', html, confirmButtonText: 'Entendi' });
        return;
    }

    if (resultado?.url) {
        toast.success('Foto atualizada!');
        return;
    }

    toast.info('Este contato não tem foto de perfil disponível.');
}
